
import React, { useState } from "react";
import { Table as UITable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, X, SortAsc, SortDesc } from "lucide-react";
import { Button } from "@/components/ui/button";
import PaginationControl from "./PaginationControl";
import { LoadingOverlay } from "./LoadingOverlay";

export interface TableColumn<T> {
  header: string;
  accessor: keyof T | ((row: T) => any);
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  className?: string;
  isLoading?: boolean;
}

function Table<T extends Record<string, any>>({
  data,
  columns,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onSearch,
  searchPlaceholder = "Buscar...",
  sortColumn,
  sortDirection,
  onSort,
  className,
  isLoading = false,
}: TableProps<T>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [hasPendingSearch, setHasPendingSearch] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setHasPendingSearch(query.length > 0);
    
    // If no search button, trigger search immediately on change
    if (!onSearch) return;
  };

  const handleSubmitSearch = () => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setHasPendingSearch(false);
    if (onSearch) {
      onSearch("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitSearch();
    }
  };

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !onSort) return;
    
    const accessor = typeof column.accessor === 'string' ? column.accessor : column.header;
    const direction = 
      sortColumn === accessor && sortDirection === 'asc' 
        ? 'desc' 
        : 'asc';
    
    onSort(accessor.toString(), direction);
  };

  const renderSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null;
    
    const accessor = typeof column.accessor === 'string' ? column.accessor : column.header;
    
    if (sortColumn === accessor) {
      return sortDirection === 'asc' 
        ? <SortAsc className="h-4 w-4 ml-1 inline-block" /> 
        : <SortDesc className="h-4 w-4 ml-1 inline-block" />;
    }
    
    return null;
  };

  const getCellValue = (row: T, column: TableColumn<T>) => {
    if (column.cell) {
      return column.cell(row);
    }

    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }

    return row[column.accessor];
  };

  return (
    <div className="space-y-4">
      {onSearch && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder={searchPlaceholder}
              className="pl-8 pr-8"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
            />
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            {hasPendingSearch && (
              <button
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                onClick={handleClearSearch}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button onClick={handleSubmitSearch}>
            Buscar
          </Button>
        </div>
      )}
      
      <div className={`rounded-md border relative ${className || ""}`}>
        <LoadingOverlay isLoading={isLoading} />
        <UITable>
          <TableHeader>
            <TableRow>
              {columns.map((column, index) => (
                <TableHead 
                  key={index}
                  className={column.sortable ? "cursor-pointer select-none" : ""}
                  onClick={() => column.sortable && handleSort(column)}
                >
                  <div className="flex items-center">
                    {column.header}
                    {renderSortIcon(column)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex}>
                      {getCellValue(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </UITable>
      </div>

      <PaginationControl
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </div>
  );
}

export default Table;
